<template>
  <div class="role-page">
    <div class="toolbar">
      <el-button type="primary" @click="openDialog">新增角色</el-button>
    </div>
    <el-table :data="list" stripe style="width:100%">
      <el-table-column prop="name" label="角色名称" />
      <el-table-column prop="description" label="角色概述" />
      <el-table-column label="操作" width="220">
        <template #default="{row}">
          <el-button size="small" @click="edit(row)">编辑</el-button>
          <el-button size="small" @click="perm(row)">权限</el-button>
          <el-button size="small" type="danger" @click="remove(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination class="pager" :current-page="pagination.page" :page-size="pagination.pageSize" :total="pagination.total" layout="prev, pager, next" @current-change="pageChange" />

    <el-dialog v-model="dialogVisible" title="角色信息" width="400px">
      <el-form :model="form" label-width="90px">
        <el-form-item label="名称"><el-input v-model="form.name" /></el-form-item>
        <el-form-item label="概述"><el-input v-model="form.description" /></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible=false">取消</el-button>
        <el-button type="primary" :loading="loading" @click="submit">确定</el-button>
      </template>
    </el-dialog>
    <el-dialog v-model="permVisible" title="角色权限" width="500px" class="role-perm-dialog" @close="handlePermDialogClose">
      <div class="perm-tree-container">
        <el-tree
          ref="treeRef"
          :data="menuTree"
          show-checkbox
          node-key="id"
          :props="{ label:'title', children:'children' }"
          :default-expand-all="false"
          :default-checked-keys="permForm.menuIds"
          check-strictly
          class="perm-tree"
          :check-on-click-node="false"
        />
      </div>
      <template #footer>
        <el-button @click="permVisible=false">取消</el-button>
        <el-button type="primary" :loading="permLoading" @click="savePerm">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, nextTick, watch } from 'vue'
import { ElMessage } from 'element-plus'
import request from '../../utils/request'

const list = ref([])
const pagination = ref({page:1,pageSize:30,total:0})
const dialogVisible = ref(false)
const loading = ref(false)
const form = ref({id:null,name:'',description:''})
const permVisible = ref(false)
const permLoading = ref(false)
const permForm = ref({roleId:null,menuIds:[]})
const menuTree = ref([])
const treeRef = ref(null)
const currentRoleId = ref(null)

const loadList = async()=>{
  const params={page:pagination.value.page,pageSize:pagination.value.pageSize}
  try{
    const data=await request.get('/v1/admin/role',{params})
    list.value=data
    pagination.value.total=data.length
  }catch(e){ElMessage.error(e.toString())}
}

const pageChange=(p)=>{pagination.value.page=p;loadList()}

const openDialog=()=>{form.value={id:null,name:'',description:''};dialogVisible.value=true}

const edit=(row)=>{form.value={id:row.id,name:row.name,description:row.description};dialogVisible.value=true}

const submit=async()=>{
  loading.value=true
  try{
    if(form.value.id){
      await request.post('/v1/admin/role/update',form.value)
      const idx=list.value.findIndex(i=>i.id===form.value.id)
      if(idx>-1) list.value[idx]={...list.value[idx],...form.value}
    }else{
      const res=await request.post('/v1/admin/role/create',form.value)
      await request.post('/v1/admin/role-menu/create',{role_id:res.id})
      list.value.push({id:res.id,name:form.value.name,description:form.value.description})
    }
    ElMessage.success('操作成功');dialogVisible.value=false
  }catch(e){ElMessage.error(e.toString())}
  finally{loading.value=false}
}

const remove=async(row)=>{
  try{
    await request.post('/v1/admin/role/delete',{id:row.id})
    await request.post('/v1/admin/role-menu/delete',{role_id:row.id})
    ElMessage.success('删除成功')
    loadList()
  }catch(e){ElMessage.error(e.toString())}
}

// 监听权限对话框打开状态
watch(permVisible, async (newVal) => {
  if (newVal && treeRef.value && permForm.value.menuIds.length > 0) {
    // 对话框打开后，等待 DOM 完全渲染
    await nextTick()
    if (treeRef.value) {
      try {
        const validIds = permForm.value.menuIds.filter(id => typeof id === 'number' && !isNaN(id) && id > 0)
        treeRef.value.setCheckedKeys(validIds, false)
      } catch (err) {
        console.error('通过 watch 设置选中状态失败:', err)
      }
    }
  }
})

// 处理权限对话框关闭
const handlePermDialogClose = () => {
  // 对话框关闭时，重置状态
  currentRoleId.value = null
  menuTree.value = []
  permForm.value = { roleId: null, menuIds: [] }
  if (treeRef.value) {
    // 清空选中状态
    try {
      treeRef.value.setCheckedKeys([], false)
    } catch (err) {
      // 忽略错误
    }
  }
}

const perm=async(row)=>{
  permForm.value.roleId=row.id
  currentRoleId.value = row.id
  try{
    // 先获取菜单树
    const res=await request.get('/v1/admin/menu/list',{params:{page:1,pageSize:999}})
    const items=res.list || []
    const map={}
    // 初始化所有节点的 children 数组，并建立 id 映射
    items.forEach(it=>{
      it.children=[]
      // 确保 parent_id 和 id 是数字类型
      // 注意：parent_id 可能为 0（根节点）、null、undefined 或数字
      const parentId = it.parent_id !== null && it.parent_id !== undefined 
        ? parseInt(it.parent_id,10) 
        : 0
      it.parent_id = isNaN(parentId) ? 0 : parentId
      it.id = parseInt(it.id,10)
      // 确保不会意外设置 disabled 属性，除非明确需要
      // 删除可能存在的 disabled 属性
      if(it.disabled !== undefined){
        delete it.disabled
      }
      map[it.id]=it
    })
    const tree=[]
    // 构建树形结构
    items.forEach(it=>{
      // 此时 parent_id 已经是数字（0 或正数）
      const parentId = it.parent_id
      // 如果 parent_id > 0 且能找到父节点，加入父节点的 children
      if(parentId > 0 && map[parentId]){
        map[parentId].children.push(it)
      }else if(parentId === 0){
        // parent_id 为 0，说明是根节点，加入 tree
        tree.push(it)
      }else{
        // 其他情况（可能是负数或 NaN），也当作根节点处理
        console.warn('菜单节点 parent_id 异常:', it.id, it.title, 'parent_id:', parentId)
        tree.push(it)
      }
    })
    // 按 sort 排序
    const sortTree=(nodes)=>{
      nodes.sort((a,b)=>(a.sort||0)-(b.sort||0))
      nodes.forEach(node=>{
        if(node.children&&node.children.length>0){
          sortTree(node.children)
        }
      })
      return nodes
    }
    menuTree.value=sortTree(tree)
    
    // 获取角色权限
    const data=await request.get('/v1/admin/role-menu/view',{params:{role_id:row.id}})
    
    // 确保 menu_id 是数字数组
    let menuIds = []
    if(data && data.menu_id !== undefined && data.menu_id !== null){
      if(Array.isArray(data.menu_id)){
        menuIds = data.menu_id.map(id=>parseInt(id,10)).filter(id=>!isNaN(id)&&id>0)
      }else if(typeof data.menu_id === 'string' && data.menu_id.trim() !== ''){
        // 如果是字符串，尝试解析
        menuIds = data.menu_id.split(',').map(id=>parseInt(id.trim(),10)).filter(id=>!isNaN(id)&&id>0)
      }else if(typeof data.menu_id === 'number'){
        menuIds = [parseInt(data.menu_id,10)].filter(id=>!isNaN(id)&&id>0)
      }
    }
    
    // 确保 menuIds 始终是数组
    if(!Array.isArray(menuIds)){
      menuIds = []
    }
    
    permForm.value.menuIds = menuIds
    
    // 打开对话框（watch 会自动处理选中状态的设置）
    permVisible.value=true
  }catch(e){
    console.error('加载权限失败:',e)
    ElMessage.error('加载权限失败：'+e.toString())
  }
}

const savePerm=async()=>{
  if(!treeRef.value){
    ElMessage.warning('权限树未加载完成')
    return
  }
  permLoading.value=true
  try{
    const checked = treeRef.value.getCheckedKeys(false)
    // 确保是数字数组
    const menuIds = Array.isArray(checked) ? checked.map(id=>parseInt(id,10)).filter(id=>!isNaN(id)&&id>0) : []
    
    await request.post('/v1/admin/role-menu/update',{
      role_id:permForm.value.roleId,
      menu_id:menuIds
    })
    ElMessage.success('保存成功')
    permVisible.value=false
  }catch(e){
    console.error('保存权限失败:',e)
    ElMessage.error('保存权限失败：'+e.toString())
  }
  finally{permLoading.value=false}
}

loadList()
</script>

<style scoped>
.role-page .toolbar{margin-bottom:15px}
.pager{margin-top:15px;text-align:right}

.role-perm-dialog :deep(.el-dialog__body) {
  padding: 20px;
  max-height: 60vh;
  overflow-y: auto;
}

.perm-tree-container {
  min-height: 300px;
  max-height: 500px;
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background-color: #fff;
}

.perm-tree :deep(.el-tree-node) {
  margin: 4px 0;
}

.perm-tree :deep(.el-tree-node__content) {
  height: 32px;
  line-height: 32px;
  padding-left: 8px;
  font-size: 14px;
}

.perm-tree :deep(.el-tree-node__content:hover) {
  background-color: #f5f7fa;
}

.perm-tree :deep(.el-checkbox) {
  margin-right: 8px;
}

.perm-tree :deep(.el-checkbox__input) {
  cursor: pointer;
}

.perm-tree :deep(.el-checkbox__input.is-disabled) {
  cursor: not-allowed;
}

.perm-tree :deep(.el-checkbox__input:not(.is-disabled)) {
  cursor: pointer;
  pointer-events: auto;
}

.perm-tree :deep(.el-tree-node__content) {
  cursor: default;
  pointer-events: auto;
}

.perm-tree :deep(.el-tree-node__content .el-checkbox) {
  pointer-events: auto;
}

.perm-tree :deep(.el-tree-node__expand-icon) {
  color: #606266;
  font-size: 14px;
  padding: 6px;
  cursor: pointer;
}

.perm-tree :deep(.el-tree-node__expand-icon.is-leaf) {
  color: transparent;
  cursor: default;
}

.perm-tree :deep(.el-tree-node__label) {
  font-size: 14px;
  color: #303133;
}

.perm-tree :deep(.el-tree-node.is-checked > .el-tree-node__content .el-tree-node__label) {
  color: #409eff;
  font-weight: 500;
}
</style>
